/**
 * IncidentReportingGuidance - Regulatory guidance for incident reporting
 * Based on WBC3 Section 3.6, MGN 564, and The Merchant Shipping (Accident Reporting and Investigation) Regulations 2012
 */

import React from 'react';
import { AlertTriangle, Info, Phone, Clock, FileText, CheckCircle } from 'lucide-react';

/**
 * Reporting thresholds based on MGN 564 and MS Regulations 2012
 * References: WBC3 Section 3.6.1, MIN 698 Amendment 1
 */
export const getReportingRequirements = (incidentData) => {
  const requirements = {
    maib: false,
    maibUrgent: false,
    mca: false,
    harbourmaster: false,
    company: true, // Always report to company SMS per WBC3 Section 31.2
    police: false,
    environmentAgency: false,
    timeframe: '30 days', // Default per WBC3
    references: []
  };

  // Check for MAIB reporting requirements (Serious Marine Casualty)
  // Reference: MGN 564 definitions and MS Regulations 2012
  if (incidentData.fatality) {
    requirements.maib = true;
    requirements.maibUrgent = true;
    requirements.timeframe = 'Immediately by fastest means';
    requirements.references.push('MGN 564: Fatality requires immediate MAIB notification');
  }

  // Check for injuries requiring external reporting
  if (incidentData.injuries) {
    // Check if medical evacuation or hospitalization required
    if (incidentData.medicalEvacuation || incidentData.hospitalization) {
      requirements.maib = true;
      requirements.maibUrgent = true;
      requirements.timeframe = 'Within 24 hours';
      requirements.references.push('MGN 564: Medical evacuation/hospitalization requires MAIB notification');
    }
    
    // Check if ambulance or emergency services called for injury
    if (incidentData.ambulanceCalled || (incidentData.emergencyServicesContacted && incidentData.injuryDetails?.includes('ambulance'))) {
      requirements.maib = true;
      requirements.timeframe = 'Within 24 hours';
      requirements.references.push('MGN 564: Injury requiring ambulance/emergency medical services may constitute serious injury');
    }
    
    // Check if coastguard medical advice sought
    if (incidentData.coastguardMedicalAdvice) {
      requirements.mca = true;
      requirements.timeframe = 'Within 24 hours';
      requirements.references.push('MGN 564: Medical advice from coastguard indicates serious medical incident');
    }
    
    // Original serious injury check
    if (incidentData.injurySeverity === 'serious') {
      // Serious injury = incapacitated for >72 hours or hospitalization >24 hours
      requirements.maib = true;
      requirements.maibUrgent = true;
      requirements.timeframe = 'Within 24 hours';
      requirements.references.push('MGN 564: Serious injury (>72h incapacity) requires MAIB notification');
    }
  }

  if (incidentData.vesselLost || incidentData.vesselAbandoned) {
    requirements.maib = true;
    requirements.maibUrgent = true;
    requirements.mca = true;
    requirements.timeframe = 'Immediately';
    requirements.references.push('MGN 564: Total loss or abandonment requires immediate notification');
  }

  if (incidentData.damage === 'substantial') {
    // Substantial damage = affects seaworthiness or structural integrity
    requirements.maib = true;
    requirements.timeframe = 'Within 24 hours';
    requirements.references.push('MGN 564: Substantial damage affecting seaworthiness');
  }

  if (incidentData.grounding || incidentData.collision) {
    requirements.maib = true;
    requirements.harbourmaster = true;
    requirements.timeframe = 'Within 24 hours';
    requirements.references.push('MGN 564: Grounding/collision requires MAIB notification');
    requirements.references.push('Local harbour regulations: Report collision/grounding to Harbourmaster');
  }

  if (incidentData.fire && incidentData.fireSeverity !== 'minor') {
    requirements.maib = true;
    requirements.timeframe = 'Within 24 hours';
    requirements.references.push('MGN 564: Fire (non-minor) requires MAIB notification');
  }

  if (incidentData.pollution || incidentData.pollutionRisk) {
    requirements.mca = true;
    requirements.harbourmaster = true;
    requirements.environmentAgency = true;
    requirements.timeframe = 'Immediately';
    requirements.references.push('MARPOL: Pollution incident immediate notification');
    requirements.references.push('MGN 632: Report pollution to authorities');
  }

  if (incidentData.navigationSafetyCompromised) {
    requirements.mca = true;
    requirements.timeframe = 'As soon as practicable';
    requirements.references.push('SOLAS: Navigation safety issues to MCA');
  }

  // Police reporting for criminal matters
  if (incidentData.theft || incidentData.assault || incidentData.vandalism) {
    requirements.police = true;
    requirements.timeframe = 'Immediately';
    requirements.references.push('Criminal matters require police notification');
  }

  return requirements;
};

/**
 * Component to display reporting guidance based on incident details
 */
const IncidentReportingGuidance = ({ incidentData, isWizardMode = false, onViewDetails }) => {
  const requirements = getReportingRequirements(incidentData);
  
  // Count how many authorities need notification
  const authoritiesToNotify = [
    requirements.maib,
    requirements.mca,
    requirements.harbourmaster,
    requirements.police,
    requirements.environmentAgency
  ].filter(Boolean).length;

  if (!isWizardMode) {
    // Compact display for the form
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-2">Reporting Requirements Assessment</p>
            {authoritiesToNotify > 0 ? (
              <>
                <p className="text-blue-700 mb-2">
                  Based on the incident details, you may need to notify {authoritiesToNotify} authorit{authoritiesToNotify === 1 ? 'y' : 'ies'}.
                </p>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                  onClick={onViewDetails}
                >
                  View detailed reporting requirements →
                </button>
              </>
            ) : (
              <p className="text-blue-700">
                Internal SMS reporting only required. No external notifications necessary based on current details.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full wizard display
  return (
    <div className="space-y-6">
      {/* Immediate Actions */}
      {(requirements.maibUrgent || incidentData.injuries || incidentData.pollution) && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-3">Immediate Actions Required</h3>
              <ol className="space-y-2 text-sm text-red-800">
                {incidentData.injuries && (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">1.</span>
                    <span>Ensure injured persons receive medical attention</span>
                  </li>
                )}
                {incidentData.pollution && (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">2.</span>
                    <span>Take action to minimize environmental impact</span>
                  </li>
                )}
                {incidentData.navigationSafetyCompromised && (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">3.</span>
                    <span>Ensure vessel and crew safety, issue navigation warnings if needed</span>
                  </li>
                )}
                {requirements.maibUrgent && (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">4.</span>
                    <span>Preserve evidence - do not disturb the scene unless for safety</span>
                  </li>
                )}
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Reporting Requirements */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Who to Report To
        </h3>
        
        <div className="space-y-4">
          {/* Always report to company */}
          <div className="border-l-4 border-blue-500 pl-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Company SMS / DPA</h4>
                <p className="text-sm text-gray-600 mt-1">
                  All incidents must be reported to your company's Safety Management System
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Reference: WBC3 Section 31.2 - Safety Management System requirements
                </p>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-blue-600">Always Required</span>
                <p className="text-xs text-gray-500 mt-1">Within {requirements.timeframe}</p>
              </div>
            </div>
          </div>

          {requirements.maib && (
            <div className={`border-l-4 ${requirements.maibUrgent ? 'border-red-500' : 'border-yellow-500'} pl-4`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">
                    MAIB (Marine Accident Investigation Branch)
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Report via: maib.gov.uk or call 023 8023 2527
                  </p>
                  <div className="mt-2 space-y-1">
                    {requirements.references
                      .filter(ref => ref.includes('MGN 564'))
                      .map((ref, idx) => (
                        <p key={idx} className="text-xs text-gray-500">• {ref}</p>
                      ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${requirements.maibUrgent ? 'text-red-600' : 'text-yellow-600'}`}>
                    {requirements.maibUrgent ? 'URGENT' : 'Required'}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">{requirements.timeframe}</p>
                </div>
              </div>
            </div>
          )}

          {requirements.mca && (
            <div className="border-l-4 border-yellow-500 pl-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">
                    MCA (Maritime & Coastguard Agency)
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Contact nearest MCA Marine Office or Coastguard
                  </p>
                  <div className="mt-2 space-y-1">
                    {requirements.references
                      .filter(ref => ref.includes('SOLAS') || ref.includes('seaworthiness'))
                      .map((ref, idx) => (
                        <p key={idx} className="text-xs text-gray-500">• {ref}</p>
                      ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-yellow-600">Required</span>
                  <p className="text-xs text-gray-500 mt-1">{requirements.timeframe}</p>
                </div>
              </div>
            </div>
          )}

          {requirements.harbourmaster && (
            <div className="border-l-4 border-yellow-500 pl-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Harbour Authority</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Contact local Harbourmaster immediately
                  </p>
                  <div className="mt-2 space-y-1">
                    {requirements.references
                      .filter(ref => ref.includes('harbour'))
                      .map((ref, idx) => (
                        <p key={idx} className="text-xs text-gray-500">• {ref}</p>
                      ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-yellow-600">Required</span>
                  <p className="text-xs text-gray-500 mt-1">Immediately</p>
                </div>
              </div>
            </div>
          )}

          {requirements.environmentAgency && (
            <div className="border-l-4 border-green-500 pl-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Environment Agency</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Pollution hotline: 0800 80 70 60
                  </p>
                  <div className="mt-2 space-y-1">
                    {requirements.references
                      .filter(ref => ref.includes('MARPOL') || ref.includes('pollution'))
                      .map((ref, idx) => (
                        <p key={idx} className="text-xs text-gray-500">• {ref}</p>
                      ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-green-600">Required</span>
                  <p className="text-xs text-gray-500 mt-1">Immediately</p>
                </div>
              </div>
            </div>
          )}

          {!authoritiesToNotify && (
            <div className="border-l-4 border-green-500 pl-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Internal Reporting Only</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Based on the incident details, only internal SMS reporting is required
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Information to Gather */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Information Required for Reports
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Vessel Information</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Vessel name and IMO/official number</li>
              <li>• Position (lat/long or location)</li>
              <li>• Port of registry</li>
              <li>• Vessel type and gross tonnage</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Incident Details</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Date, time, and location</li>
              <li>• Weather and sea conditions</li>
              <li>• Description of events</li>
              <li>• Persons involved/injured</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Consequences</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Injuries and medical treatment</li>
              <li>• Damage to vessel/equipment</li>
              <li>• Environmental impact</li>
              <li>• Effect on operations</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Actions Taken</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Immediate actions for safety</li>
              <li>• Medical assistance provided</li>
              <li>• Pollution prevention measures</li>
              <li>• Notifications made</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Preservation of Evidence */}
      {requirements.maib && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-yellow-900 mb-1">Evidence Preservation (MAIB Requirements)</p>
              <ul className="space-y-1 text-yellow-800">
                <li>• Do not disturb the scene unless necessary for safety</li>
                <li>• Preserve all logs, charts, and recordings</li>
                <li>• Take photographs of damage and scene</li>
                <li>• Secure witness statements while memories are fresh</li>
                <li>• Retain any equipment involved in the incident</li>
              </ul>
              <p className="text-xs text-yellow-700 mt-2">
                Reference: MGN 564 Section 4 - Evidence preservation requirements
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentReportingGuidance;